import React, {
    createContext,
    Dispatch,
    FC,
    FunctionComponent,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useReducer,
    useRef,
    useState,
} from 'react';

import axios, { AxiosResponse } from 'axios';
import { cloneDeep, get, isEqual, isUndefined } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Route as ReactRouterRoute, RouteProps, useLocation } from 'react-router-dom';

import { Agency, getAgency as getAgencyApi } from 'admin/agencies/Agencies';
import { getRole as getRoleApi, Role } from 'admin/roles/Roles';
import { AccountType, GetTenantsArgs, Tenant, TenantType, getTenant as getTenantApi } from 'admin/tenants/Tenants';
import { getUser as getUserApi, User } from 'admin/users/Users';
import { MessageActionType, useMessage } from 'common/message-bar/MessageBar';
import { LocaleCode, setLocale } from 'core/util/Locale';
import { Route } from 'core/util/Route';
import { route } from 'core/util/Routing';

import { _featureFlagCache } from '../admin/feature-flags/FeatureFlags';

interface Action {
    actions: string[];
    mode?: 'all' | 'any';
}

const isAction = (value: string | Action) =>
    typeof value === 'object' && value !== null && Array.isArray((value as Action).actions);

export interface ActionsAuth {
    actions?: (string | Action)[];
    featureFlags?: string[];
    mode?: 'all' | 'any';
    allowSystemUser?: boolean;
}

export enum UserType {
    TenantUser = 1,
    AgencyUser = 2,
}

export interface ProfileAgency {
    id: number;
    name: string;
    internalName: string;
    logo: string;
    loginUrl: string;
}

export interface ProfileUser {
    id: number;
    tenantID: number;
    agencyID: number;
    roleID: string;
    roleIDs: string[];
    externalID: string | null;
    name: string;
    email: string;
    phone: string;
    locale: LocaleCode;
    encryptedToken: string;
    nextPasswordReset: number;
    scopes: any[] | null;
    preferences?: { [key: string]: string };
    system: boolean;
    color?: string;
    image?: string;
    initials?: string;
    rIDs: number[];
    createdDate?: number;
    accountTypes?: String[];
}

export interface ProfileTenant {
    id: number;
    agencyID: number;
    tenantKey: string;
    name: string;
    smallLogoPath: string;
    largeLogoPath: string;
    tenantType: TenantType;
    accountType: AccountType;
    whiteLabeled: boolean;
    internalName: string;
    persistentLogin: boolean;
    menus: { name: string; url: string }[];
    supportLinks: object[] | null;
    moduleIDs: string[];
    singleSignOn: { webLoginUrl?: string; logoutUrl?: string };
    timeoutMins: number;
    tenantParameters: object[];
    defaultDateRange: string | null;
    createdDate?: number; // revert the changes
    autofindEnabled?: boolean;
    industry?: string;
    companyType?: string;
    skuIDs?: string[];
}

export interface ProfileLocation {
    id: string;
    uid: number;
    code: string;
    name: string;
    longName: string;
    address: object;
}

interface DefaultCountry {
    alpha3: string;
    code: string;
    currencyCode: string;
    currencyName: string;
    id: string;
    iso31662: string;
    name: string;
    phoneCode: string;
}
export interface Profile {
    user: ProfileUser;
    tenant?: ProfileTenant;
    location?: ProfileLocation | {};
    agency?: ProfileAgency;
    isAgencyUser: boolean;
    isSuperAgencyUser: boolean;
    isImpersonated?: boolean;
    impersonatedBy?: { agencyId: number; userId: number };
    isSingleLocation?: boolean;
    actions: { [key: string]: boolean };
    modules: { [key: string]: boolean };
    featureFlags: { [key: string]: boolean };
    persist: boolean;
    hasMultipleAccounts: boolean;
    magicLink: string;
    includeIdpUrl?: boolean;
    isSSO?: boolean;
    defaultCountry?: DefaultCountry;
}

export interface ProfileContainer {
    profile?: Profile;
    loading: boolean;
    error?: object;
}

export enum ProfileActionType {
    FetchStart,
    FetchSuccess,
    FetchError,
    SetPreferenceStart,
    SetPreferenceSuccess,
    SetPreferenceError,
}

export interface ProfileAction {
    type: ProfileActionType;
    profile?: Profile;
    error?: object;
}

interface ProfileProviderProps {
    children: ReactNode;
}

interface AuthRouteProps extends RouteProps {
    auth?: ActionsAuth;
}

export const SUPER_AGENCY_ID = 1;
export const SUPER_TENANT_ID = 1;
export const UNDEFINED_AGENCY_ID = -1;
export const UNDEFINED_TENANT_ID = -1;
export const UNDEFINED_USER_ID = -1;

let localProfile: Profile;

const ProfileStateContext = createContext<ProfileContainer | undefined>(undefined);
const ProfileDispatchContext = createContext<Dispatch<ProfileAction> | undefined>(undefined);

const profileReducer = (profileContainer: ProfileContainer, action: ProfileAction): ProfileContainer => {
    switch (action.type) {
        case ProfileActionType.FetchStart:
        case ProfileActionType.SetPreferenceStart:
            return { loading: true };

        case ProfileActionType.FetchSuccess:
        case ProfileActionType.SetPreferenceSuccess:
            return { loading: false, profile: action.profile };

        case ProfileActionType.FetchError:
        case ProfileActionType.SetPreferenceError:
            return { loading: false, error: action.error };

        default:
            throw new TypeError(`unrecognized action type ${action.type}`);
    }
};

export const ProfileProvider = ({ children }: ProfileProviderProps): JSX.Element => {
    const [state, dispatch] = useReducer(profileReducer, { loading: true });

    return (
        <ProfileStateContext.Provider value={state}>
            <ProfileDispatchContext.Provider value={dispatch}>{children}</ProfileDispatchContext.Provider>
        </ProfileStateContext.Provider>
    );
};

export const useProfileState = (): ProfileContainer => {
    const context = useContext(ProfileStateContext);

    if (context === undefined) {
        throw new Error('useProfileState must be used within a ProfileProvider');
    }

    return context;
};

const useProfileDispatch = (): Dispatch<ProfileAction> => {
    const context = useContext(ProfileDispatchContext);

    if (context === undefined) {
        throw new Error('useProfileDispatch must be used within a ProfileProvider');
    }

    return context;
};

export const useProfile = (): [ProfileContainer, Dispatch<ProfileAction>] => {
    const [profileContainer, dispatch] = [useProfileState(), useProfileDispatch()];

    useEffect(() => {
        // Fetch profile
        if (!profileContainer.profile) {
            (async (): Promise<any> => {
                try {
                    dispatch({ type: ProfileActionType.FetchStart });

                    const response: AxiosResponse = await axios.get('/api/auth/profile');

                    // Store to access locally
                    // Hooks can only be used by components
                    localProfile = response.data;
                    (window as any).profile = cloneDeep(localProfile);
                    setLocale(localProfile.user.locale);
                    dispatch({ type: ProfileActionType.FetchSuccess, profile: response.data });
                } catch (error: any) {
                    dispatch({ type: ProfileActionType.FetchError, error });
                    window.location.href = route(
                        `${Route.Auth}?path=${encodeURIComponent(window.location.pathname + window.location.search)}`,
                    );
                }
            })();
        }
    }, [profileContainer.profile, dispatch]);

    return [profileContainer, dispatch];
};

export const isAuthorized = (auth: ActionsAuth): boolean => {
    // HACK to allow Impersonated System User pass through
    const allowSystemUser = auth.allowSystemUser && localProfile.isImpersonated && localProfile.user.system;
    let authorized = false;

    if (localProfile?.actions) {
        if (allowSystemUser) {
            authorized = true;
        } else if (auth.mode === 'all') {
            // All actions
            authorized = auth.actions
                ? auth.actions.every((action: string | Action) =>
                      isAction(action)
                          ? isAuthorized({
                                ...auth,
                                actions: (action as Action).actions,
                                mode: (action as Action).mode,
                            })
                          : localProfile.actions[action as string],
                  )
                : false;
        } else {
            // Any action
            authorized = auth.actions
                ? auth.actions.some((action: string | Action) =>
                      isAction(action)
                          ? isAuthorized({
                                ...auth,
                                actions: (action as Action).actions,
                                mode: (action as Action).mode,
                            })
                          : localProfile.actions[action as string],
                  )
                : false;
        }
    }

    // When both actions and feature flags are specified
    // only check feature flags if no actions are authorized
    if (!authorized) {
        if (localProfile?.featureFlags) {
            if (auth.mode === 'all') {
                // All feature flags
                authorized = auth.featureFlags
                    ? auth.featureFlags.every((featureFlag: string) => localProfile.featureFlags[featureFlag])
                    : false;
            } else {
                // Any feature flag
                authorized = auth.featureFlags
                    ? auth.featureFlags.some((featureFlag: string) => localProfile.featureFlags[featureFlag])
                    : false;
            }
        }
    }

    return authorized;
};

export const areModulesEnabled = (modules: string[]) => {
    if (!localProfile || !localProfile.modules || !modules) {
        return false;
    }

    return modules.every((module) => {
        return !!get(localProfile.modules, module);
    });
};

export const Unauthorized: FunctionComponent = () => {
    const { t } = useTranslation(['auth']);
    const [, dispatch] = useMessage();

    useEffect(() => {
        dispatch({
            type: MessageActionType.ShowError,
            text: t('UNAUTHORIZED'),
        });
    }, [dispatch, t]);

    return <></>;
};

export const AuthRoute: FunctionComponent<AuthRouteProps> = ({ auth, children, ...rest }) => {
    return (
        <ReactRouterRoute {...rest} render={(): any => (!auth || isAuthorized(auth) ? children : <Unauthorized />)} />
    );
};

export const getAuthPage = (): string => {
    const logoutUrl =
        get(localProfile.tenant, 'singleSignOn.logoutUrl') || get(localProfile.tenant, 'singleSignOn.webLoginUrl');

    if (localProfile.isSSO && !isUndefined(logoutUrl)) {
        return logoutUrl;
    }

    let authPage = '/auth';

    if (localProfile.isAgencyUser && localProfile.agency!.internalName) {
        authPage += `?a=${localProfile!.agency!.internalName}`;

        if (localProfile.tenant?.internalName) {
            authPage += `&t=${localProfile.tenant.internalName}`;
        }
    } else if (localProfile.tenant && localProfile.tenant.internalName) {
        authPage += `?t=${localProfile.tenant.internalName}`;
    }

    return authPage;
};

export const authenticate = async (user: User, persist: boolean): Promise<Profile> => {
    const { data } = await axios.post('/api/auth/authenticate', {
        user,
        persist,
    });

    return data;
};

export const logout = async (): Promise<void> => {
    _featureFlagCache.clear();
    return axios.post('/api/auth/logout');
};

// CurrentContext

interface CurrentContextIds {
    agencyId: number;
    tenantId: number;
    userId: number;
    roleId: string;
}

type AgencyState = Map<{ agencyId: number; args: any }, Promise<Agency>>;
type TenantState = Map<{ agencyId: number; tenantId: number; args: GetTenantsArgs }, Promise<Tenant>>;
type UserState = Map<{ agencyId: number; tenantId: number; userId: number; args: any }, Promise<User>>;
type RoleState = Map<{ agencyId: number; tenantId: number; roleId: string; args: any }, Promise<Role>>;

export interface CurrentContextState extends CurrentContextIds {
    getAgency: (agencyId?: number | undefined, args?: any) => Promise<Agency>;
    getTenant: (
        agencyId?: number | undefined,
        tenantId?: number | undefined,
        args?: GetTenantsArgs | undefined,
    ) => Promise<Tenant>;
    getUser: (
        agencyId?: number | undefined,
        tenantId?: number | undefined,
        userId?: number | undefined,
        args?: any,
    ) => Promise<User>;
    getRole: (roleId?: string) => Promise<Role> | undefined;
}

export const CurrentContext = createContext<CurrentContextState | undefined>(undefined);

const getCurrentContextIds = (profile: Profile, location: any) => {
    const searchRegEx = {
        agencyId: /aid=(\d+)/,
        tenantId: /tid=(\d+)/,
        userId: /uid=(\d+)/,
        roleId: /roleId=(\w+)/,
    };

    const pathRegEx = {
        agencyId: /\/agencies\/(\d+)/,
        tenantId: /\/tenants\/(\d+)/,
        roleId: /\/roles2\/(\w+)/,
    };

    let agencyId;
    let tenantId;
    let userId;
    let roleIdMatch;

    const agencySearchMatch = location.search.match(searchRegEx.agencyId);
    const tenantSearchMatch = location.search.match(searchRegEx.tenantId);
    const userSearchMatch = location.search.match(searchRegEx.userId);
    const roleSearchMatch = location.search.match(searchRegEx.roleId);

    if (agencySearchMatch && agencySearchMatch.length === 2) {
        agencyId = Number(agencySearchMatch[1]);
    } else {
        const agencyPathMatch = location.pathname.match(pathRegEx.agencyId);

        if (agencyPathMatch && agencyPathMatch.length === 2) {
            agencyId = Number(agencyPathMatch[1]);
        } else {
            agencyId = profile.user.agencyID;
        }
    }

    if (tenantSearchMatch && tenantSearchMatch.length === 2) {
        tenantId = Number(tenantSearchMatch[1]);
    } else {
        const tenantPathMatch = location.pathname.match(pathRegEx.tenantId);

        if (tenantPathMatch && tenantPathMatch.length === 2) {
            tenantId = Number(tenantPathMatch[1]);
        } else {
            tenantId = profile.user.tenantID;
        }
    }

    if (userSearchMatch && userSearchMatch.length === 2) {
        userId = Number(userSearchMatch[1]);
    } else {
        userId = profile.user.id;
    }

    if (roleSearchMatch && roleSearchMatch.length === 2) {
        roleIdMatch = roleSearchMatch;
    } else {
        const rolePathMatch = location.pathname.match(pathRegEx.roleId);

        if (rolePathMatch && rolePathMatch.length === 2) {
            roleIdMatch = rolePathMatch;
        }
    }

    return { agencyId, tenantId, userId, roleId: roleIdMatch?.[1] };
};

export const CurrentContextProvider: FC = ({ children }) => {
    const location = useLocation();
    const [{ profile }] = useProfile();
    const [ids, setIds] = useState<CurrentContextIds>(() => getCurrentContextIds(profile!, location));
    const tenantState = useRef<TenantState>(new Map());
    const agencyState = useRef<AgencyState>(new Map());
    const userState = useRef<UserState>(new Map());
    const roleState = useRef<RoleState>(new Map());

    // effect that updates agencyId, tenantId, userId when URL changes
    useEffect(() => {
        if (!profile) return;
        setIds((prevIds) => {
            const newIds = getCurrentContextIds(profile, location);

            return isEqual(prevIds, newIds) ? prevIds : newIds;
        });
    }, [location, profile]);

    // calls getTenant API if new parameters are received or else returns previously created promise
    const getTenant = useCallback(
        (agencyId?: number, tenantId?: number, args?: GetTenantsArgs): Promise<Tenant> => {
            const allArgs = {
                agencyId: agencyId ?? ids?.agencyId!,
                tenantId: tenantId ?? ids?.tenantId!,
                args: args ?? {},
            };
            const existingArgsObject = Array.from(tenantState.current.keys()).find((key) => isEqual(key, allArgs));

            if (existingArgsObject) return tenantState.current.get(existingArgsObject)!;

            const tenantPromise = getTenantApi(allArgs.agencyId, allArgs.tenantId, allArgs.args);

            tenantState.current.set(allArgs, tenantPromise);
            return tenantPromise;
        },
        [ids],
    );

    // calls getAgency API if new parameters are received or else returns previously created promise
    const getAgency = useCallback(
        (agencyId?: number, args?: any): Promise<Agency> => {
            const allArgs = {
                agencyId: agencyId ?? ids?.agencyId!,
                args: args ?? {},
            };
            const existingArgsObject = Array.from(agencyState.current.keys()).find((key) => isEqual(key, allArgs));

            if (existingArgsObject) return agencyState.current.get(existingArgsObject)!;

            const agencyPromise = getAgencyApi(allArgs.agencyId, allArgs.args);

            agencyState.current.set(allArgs, agencyPromise);
            return agencyPromise;
        },
        [ids],
    );

    // calls getUser API if new parameters are received or else returns previously created promise
    const getUser = useCallback(
        (agencyId?: number, tenantId?: number, userId?: number, args?: any): Promise<User> => {
            const allArgs = {
                agencyId: agencyId ?? ids?.agencyId!,
                tenantId: tenantId ?? ids?.tenantId!,
                userId: userId ?? ids?.userId!,
                args: args ?? {},
            };
            const existingArgsObject = Array.from(userState.current.keys()).find((key) => isEqual(key, allArgs));

            if (existingArgsObject) return userState.current.get(existingArgsObject)!;

            const userPromise = getUserApi(allArgs.agencyId, allArgs.tenantId, allArgs.userId, allArgs.args);

            userState.current.set(allArgs, userPromise);
            return userPromise;
        },
        [ids],
    );

    // calls getRole API if new parameters are received or else returns previously created promise
    const getRole = useCallback(
        (id?: string): Promise<Role> | undefined => {
            const roleId = id ?? ids?.roleId!;

            if (!roleId) {
                return undefined;
            }
            const allArgs = {
                agencyId: ids?.agencyId!,
                tenantId: ids?.tenantId!,
                roleId,
                args: {},
            };
            const existingArgsObject = Array.from(roleState.current.keys()).find((key) => isEqual(key, allArgs));

            if (existingArgsObject) return roleState.current.get(existingArgsObject)!;

            const rolePromise = getRoleApi(allArgs.agencyId, allArgs.tenantId, allArgs.roleId, allArgs.args);

            roleState.current.set(allArgs, rolePromise);
            return rolePromise;
        },
        [ids],
    );

    return (
        <CurrentContext.Provider value={{ ...ids, getAgency, getTenant, getUser, getRole }}>
            {children}
        </CurrentContext.Provider>
    );
};

export const useCurrentContext = (): CurrentContextState => {
    const context = useContext(CurrentContext);

    if (!context) throw new Error('useCurrentContext must be used within a CurrentContextProvider');

    return context;
};
